import { ReloadOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import { Button, Card, Col, Pagination, Row, Select, Table } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaSquareFull } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import apiFactory from "../../../api";
import { MY_TASK } from "../../../config/Constant";
import { useInfoUser } from "../../../store/UserStore";
import { formatDate } from "../../../utils/formatTime";
import "../../../styles/table.scss";

const TaskList = ({ isMobile, backBtn }) => {
  const navigate = useNavigate();
  const { languageMap } = useInfoUser();
  const [taskList, setTaskList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropListSearch, setDropListSearch] = useState({
    timeType: "TODAY",
  });
  const listRef = useRef(null);
  const [pagination, setPagination] = useState({
    pageSize: MY_TASK?.MY_TASK_LIMIT,
    total: 0,
    current: 1,
  });

  const fetchTaskProjectsData = useCallback(
    async (newPagination = pagination) => {
      setLoading(true);
      try {
        const { current, pageSize } = newPagination;
        const request = {
          limit: pageSize,
          page: current,
          search: dropListSearch,
        };

        const result = await apiFactory.taskApi.getListTaskProject(request);
        if (result?.status === 200) {
          setTaskList(result?.data?.items || []);
          setPagination((prev) => ({
            ...prev,
            total: result?.data?.totalItems || 0,
          }));
        }
      } catch (error) {
        console.error("Error fetching task project data:", error);
      } finally {
        setLoading(false);
      }
    },
    [pagination.current, pagination.pageSize, dropListSearch]
  );

  useEffect(() => {
    fetchTaskProjectsData();
  }, [fetchTaskProjectsData]);

  const handleSelectChange = (value) => {
    setDropListSearch((prevState) => ({
      ...prevState,
      timeType: value,
    }));
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: "instant", block: "start" });
    }
  };

  const handleTableChange = (pagination) => {
    setPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const handleReload = () => {
    setPagination({ current: 1, pageSize: 3, total: 0 });
    setDropListSearch({ timeType: "TODAY" });
  };

  const handleTimelineTaskCodeClick = (record) => {
    navigate(
      `/work-management/timeline/${record.projectCode}?task-code=${record.taskCode}`
    );
  };

  const columns = [
    {
      align: "center",
      title: "",
      dataIndex: "color",
      key: "color",
      render: (color) => <FaSquareFull size={20} color={color} />,
      width: 50,
    },
    {
      title: languageMap?.["event.dashboard.myJob.taskName"] ?? "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      render: (text, record) => (
        <a onClick={() => handleTimelineTaskCodeClick(record)}>{text}</a>
      ),
    },
    {
      title:
        languageMap?.["event.dashboard.myJob.projectName"] ?? "Project Name",
      dataIndex: "projectName",
      key: "projectName",
    },
    {
      title: languageMap?.["event.dashboard.myJob.startDate"] ?? "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      width: 120,
      render: (date) => <span>{formatDate(date)}</span>,
    },
    {
      title: languageMap?.["event.dashboard.myJob.endDate"] ?? "End Date",
      dataIndex: "endDate",
      key: "endDate",
      width: 120,
      render: (date) => <span>{formatDate(date)}</span>,
    },
  ];

  return (
    <div
      className={`job`}
      ref={listRef}
      style={{ border: isMobile ? "none" : "" }}
    >
      <div
        className={`my-task ${isMobile ? "fixed-header" : ""}`}
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div className="flex justify-between gap-[10px]">
          {backBtn}
          <div className="font-semibold">
            {languageMap?.["event.dashboard.myJob.title"] ?? "My Job"}
          </div>
          <ReloadOutlined className="text-[20px]" onClick={handleReload} />
        </div>
        <div className="search-condition">
          <Select
            className="select-dropdown"
            value={
              languageMap?.[
                `event.dashboard.myJob.${dropListSearch?.timeType.toLowerCase()}`
              ] ?? `${dropListSearch.timeType}esss`
            }
            style={{ width: isMobile ? "100%" : 200 }}
            onChange={handleSelectChange}
            options={[
              {
                label: languageMap?.["event.dashboard.myJob.today"] ?? "To day",
                value: "TODAY",
              },
              {
                label:
                  languageMap?.["event.dashboard.myJob.tomorrow"] ?? "Tomorrow",
                value: "TOMORROW",
              },
              {
                label:
                  languageMap?.["event.dashboard.myJob.this_week"] ??
                  "This week",
                value: "THIS_WEEK",
              },
              {
                label:
                  languageMap?.["event.dashboard.myJob.this_month"] ??
                  "This month",
                value: "THIS_MONTH",
              },
            ]}
          ></Select>
        </div>
      </div>
      <div style={{ marginTop: isMobile ? "90px" : "" }}>
        {isMobile ? (
          <Row gutter={[16, 16]}>
            {taskList?.map((task) => (
              <Col
                key={task?.taskId}
                xs={24}
                sm={12}
                md={8}
                lg={6}
                xl={4}
                className=""
              >
                <Card
                  title={
                    <div className="flex items-center gap-2">
                      <FaSquareFull size={16} color={task?.color} />
                      <span
                        className="whitespace-pre-wrap"
                        style={{ color: task?.color }}
                      >
                        {task?.projectName}
                      </span>
                    </div>
                  }
                  bordered={false}
                  className="shadow-md rounded"
                  style={{ border: `1px solid #cccccc5e` }}
                >
                  <div className="p-[10px]">
                    <p className="mb-[2px]">{task?.taskName}</p>
                    <p>
                      <b>
                        {languageMap?.["event.dashboard.myProject.startDate"] ??
                          "Start Date"}
                        :
                      </b>{" "}
                      {formatDate(task?.startDate)}
                    </p>
                    <p>
                      <b>
                        {languageMap?.["event.dashboard.myProject.endDate"] ??
                          "End Date"}
                        :
                      </b>{" "}
                      {formatDate(task?.endDate)}
                    </p>
                    <div className="mt-[10px] flex justify-between gap-[5px]">
                      <Button
                        className="p-[4px]"
                        type="primary"
                        block
                        onClick={() => handleTimelineTaskCodeClick(task)}
                      >
                        {languageMap?.["task.mobile.button.viewDetails"] ?? "View Details"}
                      </Button>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}

            <div className="flex justify-center mt-4 pagination shadow-md">
              <Pagination
                current={pagination?.current}
                pageSize={pagination?.pageSize}
                total={pagination?.total}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </div>
          </Row>
        ) : (
          <Table
            className="custom-table-scroll"
            columns={columns}
            dataSource={taskList}
            pagination={pagination}
            loading={loading}
            size={"middle"}
            onChange={handleTableChange}
            rowKey={(record) => record.key}
            scroll={{
              x: 700,
              // y: 180,
            }}
            rowClassName={"dashboard-row"}
          />
        )}
      </div>
    </div>
  );
};
export { TaskList };
